import { useQuery } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import { useMemo } from "react";
import type { FhirSource, PossiblePortal } from "@/frontend/types";

export function useActiveOrgQuery() {
  return useQuery(orpc.org.getActiveOrganization.queryOptions({ input: {} }));
}

export function useActorContextQuery(enabled: boolean = true) {
  return useQuery({
    ...orpc.org.getActorContext.queryOptions({ input: {} }),
    enabled,
  });
}

export function useFhirSourcesSearchQuery(searchQuery: string) {
  const trimmedQuery = searchQuery.trim();
  return useQuery({
    ...orpc.fhirSources.search.queryOptions({ input: { query: trimmedQuery, limit: 20 } }),
    enabled: trimmedQuery.length >= 2,
  });
}

/**
 * Popular payer / aggregator sources (Aetna, Medicare, UnitedHealthcare)
 * shown by default in the patient portal picker so patients don't have to
 * search for them by name. Server enforces the type list and canonical
 * per-type row selection; see `fhirSourcesRouter.getPopular`.
 */
export function usePopularFhirSourcesQuery() {
  return useQuery(orpc.fhirSources.getPopular.queryOptions({ input: {} }));
}

export function useOnboardingSettingsQuery() {
  return useQuery(orpc.onboarding.getSettings.queryOptions({ input: {} }));
}

export function usePossiblePortalsQuery() {
  return useQuery(orpc.onboarding.getPossiblePortals.queryOptions({ input: {} }));
}

export function useConnectedAccountsQuery(enabled: boolean = true) {
  return useQuery({
    ...orpc.accounts.list.queryOptions({ input: {} }),
    enabled,
  });
}

/**
 * Returns processed source lists (connected and recommended) for the onboarding flow.
 */
export function useOnboardingSources(userId: string, possiblePortals: PossiblePortal[] | undefined, connectedAccounts: any[] | undefined) {
  // `unknown` = grant exists but no connection audit row (older or un-backfilled account); treat as connected so the reconnect flow doesn't surface an already-granted portal.
  const activeAccounts = useMemo(
    () => (connectedAccounts ?? []).filter((a) => a.status === "active" || a.status === "unknown"),
    [connectedAccounts],
  );

  const connectedProviderIds = useMemo(() => {
    return new Set(activeAccounts.map((a) => a.providerId));
  }, [activeAccounts]);

  const connectedSources = useMemo((): FhirSource[] => {
    return activeAccounts.map((a) => ({
      id: a.providerId,
      name: a.providerName,
      fhirBaseUrl: a.providerId,
      logo: a.providerLogo,
      type: a.providerType,
      portalUrl: null,
    }));
  }, [activeAccounts]);

  const recommendedSources = useMemo((): FhirSource[] => {
    const portalsArray = (possiblePortals ?? []) as PossiblePortal[];
    return portalsArray
      .filter((p) => !connectedProviderIds.has(p.fhirBaseUrl))
      .map((p) => ({
        id: p.fhirBaseUrl,
        name: p.name,
        fhirBaseUrl: p.fhirBaseUrl,
        logo: p.logo,
        type: p.type,
        portalUrl: p.portalUrl,
      }));
  }, [possiblePortals, connectedProviderIds]);

  return {
    connectedProviderIds,
    connectedSources,
    recommendedSources,
  };
}

